import React, { useCallback, useMemo } from 'react';
import { EditRedistributionData } from '../editRedistributionLib';
import { Column, DataTable } from '@ydistri/ds';
import { addToast } from '@ydistri/utils';
import { useCurrency } from '../../../../hooks/useCurrency';
import StatisticsSummary from '../StatisticsSummary';
import { SkuRedistributionResponse } from '@ydistri/api-sdk';
import { useSkuRedistributionTableColumns } from '../../Redistribution/parts/listing/components/SkuRedistributionTable/useSkuRedistributionTableColumns';
import { useDeletePickingPositionsMutation } from '../apiCalculationEdit';
import { useAppDispatch } from '../../../../store';
import { closeModal } from '../../../Modals/modalSlice';
import { processErrorAndShowToast } from '../../../../lib/utils/processErrorAndShowToast';
import { useSearchParams } from 'react-router';
import { selectSKURedistribution } from '../../../Calculations/calculationsSlice';
import BackupOrRemoveButton from '../BackupOrRemoveButton';
import { buildPickingPositionsStats } from './pickingPositionsStats';
import { computeRemSize } from '@ydistri/theme';

type EditRedistributionPickingPositionsProps = Pick<
  EditRedistributionData,
  'calculationId' | 'pickingPositions'
>;

const EditRedistributionPickingPositions: React.FC<EditRedistributionPickingPositionsProps> = ({
  calculationId,
  pickingPositions,
}) => {
  const columns = useSkuRedistributionTableColumns();
  const currency = useCurrency();
  const dispatch = useAppDispatch();
  const [deletePickingPositions, deleteStatus] = useDeletePickingPositionsMutation();
  const [, setSearchParams] = useSearchParams();

  const stats = useMemo(
    () => buildPickingPositionsStats(pickingPositions, currency),
    [pickingPositions, currency],
  );

  const handleDelete = useCallback(() => {
    deletePickingPositions({
      calculationId,
      pickingPositions: (pickingPositions ?? []).map((r, i) => ({
        sourceSkuId: r.sourceSku.skuId,
        targetSkuId: r.targetSku.skuId,
        description: i === 0 ? `Removed ${pickingPositions?.length} picking positions` : undefined,
        entityDetail: JSON.stringify(r),
      })),
    })
      .then(() => {
        setSearchParams({ p: '' });
        dispatch(selectSKURedistribution(undefined));
        dispatch(addToast({ message: `Picking positions removed successfully.` }));
        dispatch(closeModal());
      })
      .catch(processErrorAndShowToast(dispatch, 'Removing picking positions failed'));
  }, [deletePickingPositions, calculationId, pickingPositions, dispatch, setSearchParams]);

  return (
    <Column $gap="2rem" $justifyContent="space-evenly" $padding={0}>
      <div style={{ minHeight: computeRemSize(400) }}>
        <DataTable<SkuRedistributionResponse>
          id="edit-redistribution-picking-positions-table"
          showHeader={true}
          columns={columns}
          height={computeRemSize(400)}
          dataSource={pickingPositions}
        />
      </div>
      <StatisticsSummary title="Summary" stats={stats} />
      <BackupOrRemoveButton
        calculationId={calculationId}
        loading={deleteStatus.isLoading}
        onClick={handleDelete}
      />
    </Column>
  );
};

export default EditRedistributionPickingPositions;
